const DEFAULT_API_BASE_URL = 'http://localhost:4000/api';
const RUNTIME_GLOBAL_KEY = '__ESCAPE_ROOM_RUNTIME_CONFIG__';

/**
 * @typedef {Object} RuntimeConfig
 * @property {string} apiBaseUrl
 * @property {string} apiRoot
 * @property {number|null} moduleId
 * @property {number|null} tabId
 * @property {number|null} tabModuleId
 * @property {number|null} portalId
 * @property {string|null} serviceRoot
 * @property {string|null} antiForgeryToken
 * @property {{ id:number|null, username:string|null } | null} currentUser
 * @property {{ canView:boolean, canEdit:boolean, isAdmin:boolean } | null} permissions
 */

function toNumberOrNull(value) {
  if (value === undefined || value === null || value === '') return null;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
}

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value !== 'string') return false;

  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
}

function normalizeApiBaseUrl(apiBaseUrl) {
  return String(apiBaseUrl || DEFAULT_API_BASE_URL).replace(/\/$/, '');
}

function normalizePermissions(permissions) {
  if (!permissions || typeof permissions !== 'object') return null;
  return {
    canView: toBoolean(permissions.canView),
    canEdit: toBoolean(permissions.canEdit),
    isAdmin: toBoolean(permissions.isAdmin)
  };
}

function normalizeCurrentUser(currentUser) {
  if (!currentUser || typeof currentUser !== 'object') return null;
  return {
    id: toNumberOrNull(currentUser.id),
    username: currentUser.username ? String(currentUser.username) : null
  };
}

function buildBaseConfig() {
  const apiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_URL || DEFAULT_API_BASE_URL);

  return {
    apiBaseUrl,
    apiRoot: apiBaseUrl.replace(/\/api$/, ''),
    moduleId: null,
    tabId: null,
    tabModuleId: null,
    portalId: null,
    serviceRoot: null,
    antiForgeryToken: null,
    currentUser: null,
    permissions: null
  };
}

function normalizeConfig(input = {}) {
  const base = buildBaseConfig();
  const merged = {
    ...base,
    ...input
  };

  const apiBaseUrl = normalizeApiBaseUrl(merged.apiBaseUrl || base.apiBaseUrl);

  return {
    ...merged,
    apiBaseUrl,
    apiRoot: apiBaseUrl.replace(/\/api$/, ''),
    moduleId: toNumberOrNull(merged.moduleId),
    tabId: toNumberOrNull(merged.tabId),
    tabModuleId: toNumberOrNull(merged.tabModuleId),
    portalId: toNumberOrNull(merged.portalId),
    antiForgeryToken: merged.antiForgeryToken || null,
    serviceRoot: merged.serviceRoot || null,
    currentUser: normalizeCurrentUser(merged.currentUser),
    permissions: normalizePermissions(merged.permissions)
  };
}

function readHostInjectedConfig() {
  if (typeof window === 'undefined') return {};
  const injected = window[RUNTIME_GLOBAL_KEY];
  return injected && typeof injected === 'object' ? injected : {};
}

let runtimeConfig = normalizeConfig(readHostInjectedConfig());

export function initializeRuntimeConfig(overrides = {}) {
  runtimeConfig = normalizeConfig({ ...readHostInjectedConfig(), ...overrides });
  return runtimeConfig;
}

export function getRuntimeConfig() {
  return runtimeConfig;
}

export function getApiBaseUrl() {
  return runtimeConfig.apiBaseUrl;
}

export function getApiRoot() {
  return runtimeConfig.apiRoot;
}

export function getRuntimeGlobalKey() {
  return RUNTIME_GLOBAL_KEY;
}
